import React, { useEffect, useRef, useState } from 'react';
import {
  Braces, FileCode2, Server, Database, Wind,
  Code2, Atom, Zap, Flame, Activity, PenTool,
  Network, GitMerge, Sparkles, Bot
} from 'lucide-react';
import { skills, techStack } from '../mock';

const iconMap = {
  Braces, FileCode2, Server, Database, Wind,
  Code2, Atom, Zap, Flame, Activity, PenTool,
  Network, GitMerge, Sparkles, Bot
};

const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
};

const SkillBar = ({ name, level, visible, delay }) => (
  <div className="mb-4">
    <div className="flex justify-between items-center mb-2">
      <span
        className="text-sm font-medium"
        style={{ color: 'var(--text-primary)' }}
      >
        {name}
      </span>
      <span
        className="text-xs mono"
        style={{ color: 'var(--accent)' }}
      >
        {level}%
      </span>
    </div>
    <div className="skill-bar-track">
      <div
        className="skill-bar-fill"
        style={{
          width: visible ? `${level}%` : '0%',
          transitionDelay: `${delay}s`
        }}
      />
    </div>
  </div>
);

const Skills = () => {
  const [headerRef, headerVisible] = useReveal(0.2);
  const [stackRef, stackVisible] = useReveal(0.15);
  const [skillsRef, skillsVisible] = useReveal(0.1);

  return (
    <section
      id="skills"
      className="portfolio-section-alt"
      style={{ paddingTop: '100px', paddingBottom: '100px' }}
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div
          ref={headerRef}
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            marginBottom: '56px'
          }}
        >
          <p className="section-label mb-3">Tech Stack</p>
          <h2
            className="font-bold tracking-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', lineHeight: 1.12 }}
          >
            Tools I{' '}
            <span className="accent-text">Work With</span>
          </h2>
          <p
            className="mt-2 text-sm"
            style={{ color: 'var(--text-secondary)', maxWidth: '480px', lineHeight: 1.65 }}
          >
            From Laravel backends to Vue frontends, AI integrations and no-code automation — my full toolkit.
          </p>
        </div>

        {/* Tech Stack Grid (Icons) */}
        <div
          ref={stackRef}
          className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-5 gap-3 mb-16"
        >
          {techStack.map((tech, index) => {
            const Icon = iconMap[tech.icon] || Code2;
            return (
              <div
                key={tech.name}
                className="stack-badge"
                style={{
                  opacity: stackVisible ? 1 : 0,
                  transform: stackVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.55s ease ${index * 0.06}s, transform 0.55s ease ${index * 0.06}s`
                }}
              >
                <div style={{ color: 'var(--accent)' }}>
                  <Icon size={22} />
                </div>
                <span
                  className="text-xs font-medium mono text-center"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {tech.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Skill Bars Grid */}
        <div
          ref={skillsRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {skills.map((group, groupIndex) => (
            <div key={group.category}>
              <h3
                className="font-semibold mb-5 pb-2"
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-primary)',
                  borderBottom: '1px solid var(--border-color)',
                  fontFamily: "'JetBrains Mono', monospace",
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase'
                }}
              >
                {group.category}
              </h3>
              {group.items.map((skill, skillIndex) => (
                <SkillBar
                  key={skill.name}
                  name={skill.name}
                  level={skill.level}
                  visible={skillsVisible}
                  delay={groupIndex * 0.1 + skillIndex * 0.07}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
